import { CUTOFF_DAYS_BY_EVENT, type EventType } from '@/domain';

/**
 * Pure cut-off math. Given today + target date + event_type, return:
 *   - lead_days: how many calendar days between today and target
 *   - cutoff_days: required lead per event_type
 *   - is_late: lead < cutoff
 *   - urgency: 'late' | 'today' | 'urgent' (≤ cutoff) | 'normal'
 *
 * Engine NEVER touches I/O — caller passes both dates.
 */

export interface CutoffResult {
  lead_days: number;
  cutoff_days: number;
  is_late: boolean;
  urgency: 'late' | 'today' | 'urgent' | 'normal';
}

function dayDiff(from: string, to: string): number {
  const a = Date.UTC(
    Number(from.slice(0, 4)),
    Number(from.slice(5, 7)) - 1,
    Number(from.slice(8, 10)),
  );
  const b = Date.UTC(
    Number(to.slice(0, 4)),
    Number(to.slice(5, 7)) - 1,
    Number(to.slice(8, 10)),
  );
  return Math.round((b - a) / 86_400_000);
}

export function computeCutoff(
  targetDate: string,
  eventType: EventType,
  today: string,
): CutoffResult {
  const lead = dayDiff(today, targetDate);
  const cutoff = CUTOFF_DAYS_BY_EVENT[eventType] ?? 1;
  let urgency: CutoffResult['urgency'];
  if (lead < cutoff) urgency = 'late';
  else if (lead === 0) urgency = 'today';
  else if (lead <= cutoff + 1) urgency = 'urgent';
  else urgency = 'normal';
  return {
    lead_days: lead,
    cutoff_days: cutoff,
    is_late: lead < cutoff,
    urgency,
  };
}
